import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { animate, style, transition, trigger } from '@angular/animations';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { merge } from 'rxjs';
import { filter } from 'rxjs/operators';
import { PageAccessStateService, pathOf } from '../services/page-access-state.service';
import { AuthService } from '../services/auth.service';
import { NotificationService } from '../services/notification.service';
import { NotificationDto, notificationBody, notificationTitle } from '../models/notification';
import { AccountService } from '../services/account.service';
import { StudentDashboardService } from '../services/student-dashboard.service';
import { StudentMenuCounts } from '../models/student-menu-counts';
import { BrandingService } from '../services/branding.service';
import { BillingService } from '../services/billing.service';
import { LanguageService } from '../services/language.service';
import { TenantFeaturesService, TenantModule, TenantType } from '../services/tenant-features.service';
import { HomeRouteService } from '../services/home-route.service';
import { routeFor } from '../services/notification-route';

type Role = 'Administrator' | 'TenantAdmin' | 'Teacher' | 'Student' | 'Parent';

const STAFF: Role[] = ['Administrator', 'TenantAdmin', 'Teacher'];
const ADMINS: Role[] = ['Administrator', 'TenantAdmin'];
const PLATFORM: Role[] = ['Administrator'];
/** Most privileged first: with several roles, the first one held is the user's "primary" role. */
const ROLE_PRIORITY: Role[] = ['Administrator', 'TenantAdmin', 'Teacher', 'Student', 'Parent'];

/** What a visibility predicate can look at. */
interface NavContext {
  primaryRole: Role | null;
  hasRole(...roles: Role[]): boolean;
  hasModule(module: TenantModule): boolean;
  tenantType: TenantType;
}

/**
 * A single sidebar link. It shows when EVERY constraint given passes (an omitted one is no
 * constraint) AND PageAccessStateService.canAccess(route) agrees, so a link never leads to a page
 * the route guard would bounce.
 */
interface NavItem {
  /** Stable id: trackBy, `data-nav-item` (e2e) and active-state bookkeeping. */
  id: string;
  labelKey: string;
  /** Role-specific wording, picked by the user's primary role (e.g. Teacher → "My Students"). */
  labelKeyByRole?: Partial<Record<Role, string>>;
  route: string;
  queryParams?: Record<string, string>;
  /** Path prefix used for the active state / title when it differs from `route`. */
  matchPrefix?: string;
  icon: string;
  /** Only the exact path is active (no child pages). */
  exact?: boolean;
  /** The user must hold one of these roles. */
  roles?: Role[];
  /** The tenant must have at least one of these pillars on. */
  modules?: TenantModule[];
  /** The plan must not switch this add-on off. */
  entitlement?: string;
  /** Extra condition (role variants, tenant-type nuances). */
  when?: (ctx: NavContext) => boolean;
  badge?: keyof StudentMenuCounts;
}

type NavGroupKey = 'home' | 'lessons' | 'school' | 'assessment' | 'courses' | 'ai' | 'admin';

/** A sidebar section. `icon`/`accent` drive the per-section colour coding. */
interface NavGroup {
  key: NavGroupKey;
  titleKey: string;
  icon: string;
  accent: string;
  /** Group-level constraints, applied to every item of the group. */
  modules?: TenantModule[];
  entitlement?: string;
  roles?: Role[];
  items: NavItem[];
}

/** A group as rendered: only its visible items, with resolved labels. */
interface VisibleGroup {
  group: NavGroup;
  items: VisibleItem[];
}
interface VisibleItem {
  item: NavItem;
  label: string;
}

// ── The sidebar ─────────────────────────────────────────────────────────────
// One group per product pillar (Private Lessons = tutoring, School, Courses = LMS) plus Home,
// Assessment (school or LMS), AI tools and Administration. See the "Sidebar design" in the plan.

/** Teachers in a pure tutoring tenant get "Today" instead of the dashboard; parents get "My Children". */
function showDashboard(ctx: NavContext): boolean {
  if (ctx.primaryRole === 'Parent') return false;
  if (ctx.primaryRole === 'Teacher') return !ctx.hasModule('tutoring') || ctx.hasModule('school');
  return true;
}
/** "Today" is the tutoring teacher's home; a solo tutor running their own academy is its TenantAdmin. */
function showToday(ctx: NavContext): boolean {
  return ctx.primaryRole === 'Teacher'
    || (ctx.primaryRole === 'TenantAdmin' && ctx.tenantType === TenantType.TutoringAcademy);
}
const isStaffPrimary = (ctx: NavContext) =>
  ctx.primaryRole === 'Administrator' || ctx.primaryRole === 'TenantAdmin' || ctx.primaryRole === 'Teacher';

const NAV_GROUPS: NavGroup[] = [
  {
    key: 'home', titleKey: 'navigation.GroupHome', icon: 'bi bi-house-door-fill', accent: '#3b82f6',
    items: [
      { id: 'today', labelKey: 'navigation.Today', route: '/lessons/today', icon: 'bi bi-sun',
        modules: ['tutoring'], roles: ['Teacher', 'TenantAdmin'], when: showToday },
      { id: 'children', labelKey: 'navigation.MyChildren', route: '/parent', icon: 'ico-students', roles: ['Parent'] },
      { id: 'dashboard', labelKey: 'navigation.Dashboard', route: '/account/dashboard', icon: 'ico-dashboard',
        exact: true, when: showDashboard },
      { id: 'calendar', labelKey: 'navigation.Calendar', route: '/calendar', icon: 'bi bi-calendar3' },
      { id: 'messages', labelKey: 'navigation.Messages', route: '/messages', icon: 'bi bi-chat-dots' },
      { id: 'notifications', labelKey: 'navigation.Notifications', route: '/notifications', icon: 'bi bi-bell' },
    ],
  },
  {
    key: 'lessons', titleKey: 'navigation.GroupPrivateLessons', icon: 'bi bi-person-video3', accent: '#8b5cf6',
    modules: ['tutoring'],
    items: [
      { id: 'tutor-students', labelKey: 'navigation.PrivateStudents', labelKeyByRole: { Teacher: 'navigation.MyStudents' },
        route: '/lessons/students', icon: 'ico-students', roles: STAFF },
      { id: 'lesson-schedule', labelKey: 'navigation.Schedule', route: '/lessons/schedule', icon: 'bi bi-calendar-week', roles: STAFF },
      { id: 'bookings', labelKey: 'navigation.BookingRequests', route: '/academy/bookings', icon: 'bi bi-inbox', roles: STAFF },
      { id: 'availability', labelKey: 'navigation.Availability', route: '/academy/availability', icon: 'bi bi-clock', roles: STAFF },
      { id: 'inquiries', labelKey: 'navigation.Inquiries', route: '/academy/inquiries', icon: 'bi bi-envelope-open', roles: STAFF },
      { id: 'lesson-payments', labelKey: 'navigation.Payments', route: '/academy/payments', icon: 'bi bi-cash-coin', roles: STAFF },
      { id: 'public-profile', labelKey: 'navigation.PublicProfile', route: '/academy/profile', icon: 'bi bi-person-badge',
        roles: ['Teacher', 'TenantAdmin'] },
      { id: 'classroom', labelKey: 'navigation.VirtualClassroom', route: '/classroom', icon: 'bi bi-camera-video',
        roles: STAFF, entitlement: 'liveClassroom' },
      { id: 'my-lessons', labelKey: 'navigation.MyLessons', route: '/lessons/my', icon: 'bi bi-journal-check', roles: ['Student'] },
      { id: 'family-lessons', labelKey: 'navigation.Lessons', route: '/lessons/family', icon: 'bi bi-journal-check', roles: ['Parent'] },
    ],
  },
  {
    key: 'school', titleKey: 'navigation.GroupSchool', icon: 'bi bi-building', accent: '#10b981',
    modules: ['school'],
    items: [
      { id: 'students', labelKey: 'navigation.Students', route: '/student/studentlist', icon: 'ico-students', roles: ADMINS },
      { id: 'my-classes', labelKey: 'navigation.MyClasses', route: '/teacher-panel', queryParams: { tab: 'students' },
        icon: 'ico-students', roles: ['Teacher'], when: (c) => c.primaryRole === 'Teacher' },
      { id: 'teachers', labelKey: 'navigation.Teachers', route: '/teachers/teacherlist', icon: 'ico-students', roles: ADMINS },
      { id: 'grades', labelKey: 'navigation.Grades', route: '/lookup/grade', icon: 'ico-groups', roles: ADMINS },
      { id: 'groups', labelKey: 'navigation.Groups', route: '/lookup/studentgroup', icon: 'ico-groups', roles: STAFF },
      { id: 'timetable', labelKey: 'navigation.Timetable', route: '/school/timetable', icon: 'bi bi-calendar3-week', roles: STAFF },
      { id: 'my-timetable', labelKey: 'navigation.MyTimetable', route: '/student/timetable', icon: 'bi bi-calendar3-week',
        roles: ['Student'], badge: 'timetable' },
      { id: 'attendance', labelKey: 'navigation.Attendance', route: '/teacher-panel', queryParams: { tab: 'attendance' },
        icon: 'bi bi-person-check', roles: STAFF },
      { id: 'excuses', labelKey: 'navigation.AbsenceExcuses', route: '/school/excuses', icon: 'ico-files-check', roles: STAFF },
      { id: 'absences', labelKey: 'navigation.Absences', route: '/school/absences', icon: 'ico-files-check',
        roles: ['Student', 'Parent'], badge: 'excuses' },
      { id: 'announcements', labelKey: 'navigation.Announcements', route: '/school/announcements', icon: 'bi bi-megaphone' },
      { id: 'manage-lectures', labelKey: 'navigation.ManageLectures', route: '/lectures/lecturemanagementlist', icon: 'ico-lectures', roles: STAFF },
      { id: 'my-lectures', labelKey: 'navigation.MyLectures', route: '/lectures/mylecturelist', icon: 'ico-lectures', roles: ['Student'] },
      // Live classes: staff find them under Private Lessons when tutoring is on; students always here.
      { id: 'live-classes', labelKey: 'navigation.VirtualClassroom', route: '/classroom', icon: 'bi bi-camera-video',
        roles: ['Administrator', 'TenantAdmin', 'Teacher', 'Student'], entitlement: 'liveClassroom',
        when: (c) => c.primaryRole === 'Student' || (isStaffPrimary(c) && !c.hasModule('tutoring')) },
      { id: 'reports', labelKey: 'navigation.Reports', route: '/school/reports', icon: 'bi bi-file-earmark-bar-graph', roles: STAFF },
    ],
  },
  {
    key: 'assessment', titleKey: 'navigation.GroupAssessment', icon: 'bi bi-clipboard-check-fill', accent: '#f43f5e',
    modules: ['school', 'lms'],
    items: [
      { id: 'exams', labelKey: 'navigation.Exams', route: '/teacher-panel', queryParams: { tab: 'exams' }, icon: 'ico-quizzes', roles: STAFF },
      { id: 'assignments', labelKey: 'navigation.Assignments', route: '/teacher-panel', queryParams: { tab: 'assignments' },
        icon: 'ico-files-check', roles: STAFF },
      { id: 'quizzes', labelKey: 'navigation.Quizzes', route: '/quizzes', icon: 'ico-quizzes', roles: STAFF },
      { id: 'question-bank', labelKey: 'navigation.QuestionBank', route: '/question-bank', icon: 'ico-question-bank', roles: STAFF },
      { id: 'grading', labelKey: 'navigation.GradingQueue', route: '/assessments/grading', icon: 'bi bi-pencil-square', roles: STAFF },
      { id: 'rubrics', labelKey: 'navigation.Rubrics', route: '/assessments/rubrics', icon: 'bi bi-grid-3x3-gap', roles: STAFF },
      { id: 'gradebook', labelKey: 'navigation.Gradebook', route: '/gradebook', icon: 'ico-files-check', roles: STAFF, modules: ['school'] },
      { id: 'grade-weights', labelKey: 'navigation.GradeWeights', route: '/assessments/gradebook', icon: 'bi bi-percent', roles: STAFF },
      { id: 'my-exams', labelKey: 'navigation.MyExams', route: '/student/exams', icon: 'ico-quizzes', roles: ['Student'], badge: 'exams' },
      { id: 'my-quizzes', labelKey: 'navigation.MyQuizzes', route: '/student/quizzes', icon: 'ico-quizzes', roles: ['Student'], badge: 'quizzes' },
      { id: 'my-assignments', labelKey: 'navigation.MyAssignments', route: '/student/assignments', icon: 'ico-files-check',
        roles: ['Student'], badge: 'assignments' },
      { id: 'my-grades', labelKey: 'navigation.MyGrades', route: '/assessments/my-grades', icon: 'bi bi-percent', roles: ['Student'] },
      { id: 'report-card', labelKey: 'navigation.MyReportCard', route: '/gradebook/report-card', icon: 'ico-files-check',
        roles: ['Student'], modules: ['school'] },
      { id: 'my-progress', labelKey: 'navigation.MyProgress', route: '/student/analytics', icon: 'bi bi-graph-up', roles: ['Student'] },
      { id: 'achievements', labelKey: 'navigation.MyAchievements', route: '/achievements', icon: 'bi bi-trophy', roles: ['Student'] },
    ],
  },
  {
    key: 'courses', titleKey: 'navigation.GroupCourses', icon: 'bi bi-journal-richtext', accent: '#06b6d4',
    modules: ['lms'], entitlement: 'lms',
    items: [
      { id: 'courses', labelKey: 'navigation.Courses', route: '/courses', icon: 'bi bi-journal-richtext', roles: STAFF },
      { id: 'my-courses', labelKey: 'navigation.MyCourses', route: '/courses/my', icon: 'bi bi-journal-richtext', roles: ['Student'] },
      { id: 'learning-paths', labelKey: 'navigation.LearningPaths', route: '/courses/paths', icon: 'bi bi-signpost-split', roles: STAFF },
      { id: 'my-paths', labelKey: 'navigation.MyLearningPaths', route: '/courses/my-paths', icon: 'bi bi-signpost-split', roles: ['Student'] },
      { id: 'catalog', labelKey: 'navigation.Catalog', route: '/courses/catalog', icon: 'bi bi-grid',
        roles: ['Administrator', 'TenantAdmin', 'Teacher', 'Student'] },
      { id: 'content-library', labelKey: 'navigation.ContentLibrary', route: '/courses/library', icon: 'bi bi-collection', roles: STAFF },
      { id: 'certificate-templates', labelKey: 'navigation.CertificateTemplates', route: '/courses/certificate-templates',
        icon: 'bi bi-award', roles: ADMINS },
      { id: 'my-certificates', labelKey: 'navigation.MyCertificates', route: '/courses/certificates', icon: 'bi bi-award', roles: ['Student'] },
    ],
  },
  {
    key: 'ai', titleKey: 'navigation.GroupAiTools', icon: 'bi bi-stars', accent: '#a855f7',
    entitlement: 'ai', roles: STAFF,
    items: [
      { id: 'teaching-assistant', labelKey: 'navigation.TeachingAssistant', route: '/teaching-assistant', icon: 'bi bi-robot' },
      { id: 'lesson-presentations', labelKey: 'navigation.LessonPresentations', route: '/lesson-presentations/list',
        matchPrefix: '/lesson-presentations', icon: 'bi bi-easel' },
      { id: 'ai-insights', labelKey: 'navigation.AiInsights', route: '/ai/insights', icon: 'bi bi-lightbulb' },
    ],
  },
  {
    key: 'admin', titleKey: 'navigation.GroupAdministration', icon: 'bi bi-gear-fill', accent: '#f59e0b',
    roles: ADMINS,
    items: [
      { id: 'settings', labelKey: 'navigation.GeneralSettings', route: '/settings', icon: 'ico-settings', exact: true },
      { id: 'modules', labelKey: 'navigation.Modules', route: '/settings/modules', icon: 'bi bi-toggles' },
      { id: 'accounts', labelKey: 'navigation.Accounts', route: '/settings/accounts', icon: 'ico-file-person' },
      { id: 'billing', labelKey: 'navigation.Billing', route: '/settings/billing', icon: 'ico-payment' },
      { id: 'branding', labelKey: 'navigation.Branding', route: '/settings/branding', icon: 'bi bi-palette', entitlement: 'branding' },
      { id: 'page-access', labelKey: 'navigation.PageAccess', route: '/settings/page-access', icon: 'ico-files-check' },
      { id: 'user-approval', labelKey: 'navigation.UserApproval', route: '/settings/user-approval', icon: 'ico-file-person' },
      { id: 'bulk-import', labelKey: 'navigation.BulkImport', route: '/settings/bulk-import', icon: 'bi bi-upload' },
      // Platform (Administrator only)
      { id: 'ai-integrations', labelKey: 'navigation.AiIntegrations', route: '/settings/ai-integrations', icon: 'bi bi-cpu', roles: PLATFORM },
      { id: 'landing-cms', labelKey: 'navigation.LandingPage', route: '/settings/landing-cms', icon: 'bi bi-window', roles: PLATFORM },
      { id: 'subscriptions', labelKey: 'navigation.Subscriptions', route: '/settings/subscriptions', icon: 'ico-payment', roles: PLATFORM },
      { id: 'tenants', labelKey: 'navigation.Tenants', route: '/account/tenant-list', icon: 'ico-groups', roles: PLATFORM },
      { id: 'tenant-management', labelKey: 'navigation.TenantManagement', route: '/account/tenant-management', icon: 'ico-groups', roles: PLATFORM },
      { id: 'tenant-signups', labelKey: 'navigation.TenantSignups', route: '/account/tenant-signups', icon: 'bi bi-person-plus', roles: PLATFORM },
    ],
  },
];

/** Group order per tenant type: the tenant's main pillar comes right after Home. */
const GROUP_ORDER: Record<TenantType, NavGroupKey[]> = {
  [TenantType.Mixed]: ['home', 'lessons', 'school', 'assessment', 'courses', 'ai', 'admin'],
  [TenantType.TutoringAcademy]: ['home', 'lessons', 'courses', 'assessment', 'school', 'ai', 'admin'],
  [TenantType.School]: ['home', 'school', 'assessment', 'lessons', 'courses', 'ai', 'admin'],
  [TenantType.TrainingProvider]: ['home', 'courses', 'assessment', 'lessons', 'school', 'ai', 'admin'],
};

/**
 * Topbar titles for pages with no sidebar entry of their own (or whose entry is hidden for the
 * user). The longest prefix wins, and a longer match here beats a shorter sidebar match.
 */
const EXTRA_TITLES: { prefix: string; labelKey: string }[] = [
  { prefix: '/account/dashboard', labelKey: 'navigation.Dashboard' },
  { prefix: '/parent', labelKey: 'navigation.MyChildren' },
  { prefix: '/notifications', labelKey: 'navigation.Notifications' },
  { prefix: '/lessons', labelKey: 'navigation.GroupPrivateLessons' },
  { prefix: '/lessons/today', labelKey: 'navigation.Today' },
  { prefix: '/lessons/students', labelKey: 'navigation.PrivateStudents' },
  { prefix: '/lessons/schedule', labelKey: 'navigation.Schedule' },
  { prefix: '/lessons/my', labelKey: 'navigation.MyLessons' },
  { prefix: '/lessons/family', labelKey: 'navigation.Lessons' },
  { prefix: '/academy', labelKey: 'navigation.AcademyDesk' },
  { prefix: '/school', labelKey: 'navigation.GroupSchool' },
  { prefix: '/teacher-panel', labelKey: 'navigation.TeacherPanel' },
  { prefix: '/gradebook/report-card', labelKey: 'navigation.ReportCard' },
  { prefix: '/student/register', labelKey: 'navigation.RegisterStudent' },
  { prefix: '/teachers/register', labelKey: 'navigation.RegisterTeacher' },
  { prefix: '/courses/learn', labelKey: 'navigation.CoursePlayer' },
  { prefix: '/courses/builder', labelKey: 'navigation.CourseBuilder' },
  { prefix: '/courses/new', labelKey: 'navigation.NewCourse' },
  { prefix: '/courses/categories', labelKey: 'navigation.CourseCategories' },
  { prefix: '/classroom', labelKey: 'navigation.VirtualClassroom' },
  { prefix: '/classroom/room', labelKey: 'navigation.LiveRoom' },
  { prefix: '/classroom/recordings', labelKey: 'navigation.Recordings' },
  { prefix: '/lesson-presentations', labelKey: 'navigation.LessonPresentations' },
  { prefix: '/lesson-presentations/create', labelKey: 'navigation.NewPresentation' },
  { prefix: '/lesson-presentations/view', labelKey: 'navigation.Presentation' },
  { prefix: '/quizzes/new', labelKey: 'navigation.NewQuiz' },
  { prefix: '/quizzes/edit', labelKey: 'navigation.EditQuiz' },
  { prefix: '/assessments/assignment', labelKey: 'assessments.RubricGrading' },
  { prefix: '/assessments/quiz-results', labelKey: 'assessments.QuizResults' },
  { prefix: '/assessments/attempt', labelKey: 'assessments.AttemptReview' },
  { prefix: '/settings/messaging', labelKey: 'navigation.EmailMessaging' },
  { prefix: '/settings/live-classroom', labelKey: 'navigation.LiveClassroomSettings' },
  { prefix: '/ai', labelKey: 'navigation.GroupAiTools' },
];

/** Bump when the group keys change, so stale open/closed state is ignored. */
const OPEN_GROUPS_STORAGE_KEY = 'navOpenGroups.v2';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
  animations: [
    // Matches the Figma toggle: height 0 → auto with a fade and 4px slide,
    // 220ms on the standard cubic-bezier(.4, 0, .2, 1) curve.
    trigger('expandGroup', [
      transition(':enter', [
        style({ height: 0, opacity: 0, transform: 'translateY(-4px)' }),
        animate(
          '220ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ height: '*', opacity: 1, transform: 'translateY(0)' }),
        ),
      ]),
      transition(':leave', [
        animate(
          '220ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ height: 0, opacity: 0, transform: 'translateY(-4px)' }),
        ),
      ]),
    ]),
  ],
})
export class LayoutComponent implements OnInit {
  isSidebarCollapsed = false;
  /** Mobile off-canvas sidebar state (<992px); ignored on desktop. */
  sidebarOpen = false;
  selectedText = '';

  /** The groups and items this user may see, in this tenant's order (rebuilt on every change). */
  visibleGroups: VisibleGroup[] = [];
  /** The sidebar item that matches the current URL best (longest visible prefix), or null. */
  activeItemId: string | null = null;
  /** The group holding the active item; it opens automatically. */
  activeGroupKey: NavGroupKey | null = null;
  /** The user's landing page (brand tile link). */
  homeUrl = '/account/dashboard';

  private openGroups = new Set<string>();
  /** An auto-opened active group the user collapsed by hand (until the active group changes). */
  private collapsedActiveGroup: string | null = null;
  private currentUrl = '';

  unreadCount = 0;
  recentNotifications: NotificationDto[] = [];

  /** Student sidebar badge counts (zeros until loaded / for non-students). */
  studentCounts: StudentMenuCounts = { exams: 0, quizzes: 0, assignments: 0, excuses: 0, timetable: 0 };

  private readonly destroyRef = inject(DestroyRef);

  get currentLang(): string {
    return this.language.current;
  }

  /** Long, localized "Tuesday, 1 July 2025" shown next to the page title in the topbar. */
  get today(): string {
    const locale = this.currentLang === 'ar' ? 'ar-EG' : 'en-GB';
    return new Date().toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  }

  constructor(
    private router: Router,
    public translate: TranslateService,
    public pageAccess: PageAccessStateService,
    public auth: AuthService,
    private notifService: NotificationService,
    private studentDashboard: StudentDashboardService,
    public branding: BrandingService,
    public billing: BillingService,
    private language: LanguageService,
    private account: AccountService,
    private features: TenantFeaturesService,
    private homeRoute: HomeRouteService,
  ) {}

  ngOnInit(): void {
    this.loadNotificationSummary();
    if (this.auth.hasAnyRole('Student')) {
      this.loadStudentCounts();
    }
    // Load billing status for the banner + entitlement-based menu hiding.
    if (this.auth.hasAnyRole('Administrator', 'TenantAdmin')) {
      this.billing.loadSummary().subscribe({ error: () => {} });
    }

    this.currentUrl = this.router.url;
    this.rebuildNav();
    this.restoreOpenGroups();

    // Rebuild when anything that decides visibility changes: the tenant's modules/plan (e.g. after
    // saving Settings → Modules), the page-access matrix, the billing summary, or the language
    // (labels are resolved eagerly). onLangChange fires once the new translation file is loaded.
    merge(
      this.features.features$,
      this.pageAccess.changed$,
      this.billing.summary,
      this.translate.onLangChange,
      this.language.lang$,
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.rebuildNav());

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
      .subscribe((e) => {
        this.currentUrl = e.urlAfterRedirects;
        this.sidebarOpen = false;
        this.syncActiveFromUrl();
      });
  }

  // ── grouped navigation ─────────────────────────────────────────────────────

  private get context(): NavContext {
    const roles = this.auth.getUserRoles();
    return {
      primaryRole: ROLE_PRIORITY.find((r) => roles.includes(r)) ?? null,
      hasRole: (...wanted: Role[]) => wanted.some((r) => roles.includes(r)),
      hasModule: (m: TenantModule) => this.features.hasModule(m),
      tenantType: this.features.current?.tenantType ?? TenantType.Mixed,
    };
  }

  /** True if the item (inside `group`) passes every constraint for the current user and tenant. */
  private canShow(group: NavGroup, item: NavItem, ctx: NavContext): boolean {
    for (const c of [group, item]) {
      if (c.roles && !ctx.hasRole(...c.roles)) return false;
      if (c.modules && !c.modules.some((m) => ctx.hasModule(m))) return false;
      if (c.entitlement && !this.billing.isEnabled(c.entitlement)) return false;
    }
    if (item.when && !item.when(ctx)) return false;
    // A page that is not part of this build (its feature module is not routed) is never linked.
    if (!this.homeRoute.isRouted(item.route)) return false;
    // Finally the route guard's own verdict (page key, module, entitlement and role rules).
    return this.pageAccess.canAccess(item.route);
  }

  private labelFor(item: NavItem, ctx: NavContext): string {
    const key = (ctx.primaryRole && item.labelKeyByRole?.[ctx.primaryRole]) || item.labelKey;
    return this.translate.instant(key);
  }

  /** Recomputes the visible groups (order, items, labels), then the active item and title. */
  private rebuildNav(): void {
    const ctx = this.context;
    const order = GROUP_ORDER[ctx.tenantType] ?? GROUP_ORDER[TenantType.Mixed];
    this.visibleGroups = order
      .map((key) => NAV_GROUPS.find((g) => g.key === key)!)
      .map((group) => ({
        group,
        items: group.items
          .filter((item) => this.canShow(group, item, ctx))
          .map((item) => ({ item, label: this.labelFor(item, ctx) })),
      }))
      .filter((g) => g.items.length > 0);
    this.homeUrl = this.homeRoute.current();
    this.syncActiveFromUrl();
  }

  /** How well `item` matches the URL: -1 for no match, else higher = more specific. */
  private matchScore(item: NavItem, path: string, query: Record<string, string>): number {
    const prefix = item.matchPrefix ?? item.route;
    const matches = item.exact ? path === prefix : path === prefix || path.startsWith(prefix + '/');
    if (!matches) return -1;
    let score = prefix.length * 10;
    if (item.queryParams) {
      for (const [k, v] of Object.entries(item.queryParams)) {
        if (query[k] !== v) return -1;
        score += 1;
      }
    }
    return score;
  }

  /** Active item, auto-open group and topbar title — from VISIBLE items only. */
  private syncActiveFromUrl(): void {
    const tree = this.router.parseUrl(this.currentUrl || '/');
    const path = pathOf(this.currentUrl);
    const query = tree.queryParams as Record<string, string>;

    let best: { item: VisibleItem; group: NavGroupKey; score: number } | null = null;
    for (const g of this.visibleGroups) {
      for (const vi of g.items) {
        const score = this.matchScore(vi.item, path, query);
        if (score >= 0 && (!best || score > best.score)) best = { item: vi, group: g.group.key, score };
      }
    }

    const previousGroup = this.activeGroupKey;
    this.activeItemId = best?.item.item.id ?? null;
    this.activeGroupKey = best?.group ?? null;
    if (this.activeGroupKey !== previousGroup) this.collapsedActiveGroup = null;

    // Title: the best sidebar match, unless a page-specific title matches a longer prefix.
    let title = best?.item.label ?? null;
    let titleScore = best?.score ?? -1;
    for (const t of EXTRA_TITLES) {
      if (path === t.prefix || path.startsWith(t.prefix + '/')) {
        const score = t.prefix.length * 10;
        if (score > titleScore) {
          titleScore = score;
          title = this.translate.instant(t.labelKey);
        }
      }
    }
    this.selectedText = title ?? (this.branding.name || this.translate.instant('app.Menassaty'));
  }

  isGroupOpen(group: NavGroup): boolean {
    if (this.openGroups.has(group.key)) return true;
    return this.activeGroupKey === group.key && this.collapsedActiveGroup !== group.key;
  }

  toggleGroup(group: NavGroup): void {
    if (this.isGroupOpen(group)) {
      this.openGroups.delete(group.key);
      if (this.activeGroupKey === group.key) this.collapsedActiveGroup = group.key;
    } else {
      this.openGroups.add(group.key);
      if (this.collapsedActiveGroup === group.key) this.collapsedActiveGroup = null;
    }
    this.saveOpenGroups();
  }

  /** Remembered open groups; by default Home plus the tenant's first pillar group. */
  private restoreOpenGroups(): void {
    let saved: unknown = null;
    try {
      saved = JSON.parse(localStorage.getItem(OPEN_GROUPS_STORAGE_KEY) || 'null');
    } catch {
      saved = null;
    }
    if (Array.isArray(saved)) {
      this.openGroups = new Set(saved.filter((k) => typeof k === 'string'));
      return;
    }
    const firstPillar = this.visibleGroups.find((g) => g.group.key !== 'home')?.group.key;
    this.openGroups = new Set(['home', ...(firstPillar ? [firstPillar] : [])]);
  }

  private saveOpenGroups(): void {
    try {
      localStorage.setItem(OPEN_GROUPS_STORAGE_KEY, JSON.stringify([...this.openGroups]));
    } catch {
      /* storage unavailable: open state just isn't remembered */
    }
  }

  trackGroup(_: number, g: VisibleGroup): string {
    return g.group.key;
  }
  trackItem(_: number, vi: VisibleItem): string {
    return vi.item.id;
  }

  /** Per-section CSS custom properties (accent + the derived translucent tints). */
  groupAccentVars(group: NavGroup): Record<string, string> {
    return {
      '--accent': group.accent,
      '--accent-soft': group.accent + '28',
      '--accent-line': group.accent + '40',
    };
  }

  /** The footer user card only links to Settings when that page is reachable. */
  get settingsVisible(): boolean {
    return this.pageAccess.canAccess('/settings');
  }

  badgeCount(item: NavItem): number {
    return item.badge ? (this.studentCounts[item.badge] ?? 0) : 0;
  }

  get billingNotice(): string | null {
    const s = this.billing.current;
    // Banner only for non-active states (trial/grace/read-only).
    return s?.subscription && s.subscription.status !== 2 ? s.notice ?? null : null;
  }
  get billingReadOnly(): boolean {
    return this.billing.current?.accessLevel === 2;
  }

  loadStudentCounts(): void {
    this.studentDashboard.getMenuCounts().subscribe({
      next: res => {
        if (res.isSuccess && res.value) this.studentCounts = res.value;
      },
      error: () => {},
    });
  }

  loadNotificationSummary(): void {
    this.notifService.getSummary().subscribe({
      next: res => {
        if (res.isSuccess && res.value) {
          this.unreadCount = res.value.unreadCount;
          this.recentNotifications = res.value.recent;
        }
      },
      error: () => {},
    });
  }

  markAllRead(): void {
    this.notifService.markAllRead().subscribe({
      next: res => {
        if (res.isSuccess) {
          this.unreadCount = 0;
          this.recentNotifications.forEach(n => (n.isRead = true));
        }
      },
      error: () => {},
    });
  }

  /** Bell click: mark the notification read, then open the page it is about (if any). */
  openNotification(n: NotificationDto): void {
    this.markOneRead(n);
    const url = routeFor(n, this.auth.getUserRoles());
    if (url) this.router.navigateByUrl(url);
  }

  markOneRead(n: NotificationDto): void {
    if (n.isRead) return;
    this.notifService.markRead(n.id).subscribe({
      next: res => {
        if (res.isSuccess) {
          n.isRead = true;
          this.unreadCount = Math.max(0, this.unreadCount - 1);
        }
      },
      error: () => {},
    });
  }

  toggleSidebar() {
    this.sidebarOpen = !this.sidebarOpen;
  }

  closeSidebar() {
    this.sidebarOpen = false;
  }

  /** Notification text in the UI language (Arabic when available and the UI is Arabic). */
  notifTitle(n: NotificationDto): string {
    return notificationTitle(n, this.currentLang);
  }
  notifBody(n: NotificationDto): string {
    return notificationBody(n, this.currentLang);
  }

  logout(): void {
    // AccountService.logout clears the token AND every per-user cache (page access, tenant
    // features, billing summary) so the next user in this tab never inherits this one's menu.
    this.account.logout();
  }

  toggleLang() {
    this.language.toggle();
  }
}
